import { Portal } from '@design/components/portal';
import clsx from 'clsx';
import React, { HTMLAttributes, ReactNode, useEffect } from 'react';

import { Button } from '../button/Button';
import styles from './Drawer.module.scss';

export const DRAWER_WIDTH = 600;

export enum CloseTrigger {
  CloseButtonClick = 'CloseButtonClick',
  BackgroundClick = 'BackgroundClick',
  EscapeKey = 'EscapeKey',
}

export interface DrawerProps extends HTMLAttributes<HTMLElement> {
  /** Element rendered in the header next to the close icon (e.g. a back button). Ignored if `customHeader` is set. */
  headerButton?: ReactNode;
  /** Controls visibility. Drives the slide-in/out animation; the drawer is always mounted. */
  isOpen: boolean;
  /** Called when any of the configured `closeTriggers` fires. */
  handleCloseClick: () => void;
  /** Override for the background-click trigger. When set, this is called instead of `handleCloseClick`. */
  customBackgroundClickHandler?: () => void;
  /**
   * Which interactions trigger a close. Accepts a single `CloseTrigger`
   * or an array. Defaults to all three: `CloseButtonClick`,
   * `BackgroundClick`, and `EscapeKey`.
   */
  closeTriggers?: CloseTrigger | CloseTrigger[];
  /** Show the built-in close button in the default header. Defaults to `true`. Ignored if `customHeader` is set. */
  closeIcon?: boolean;
  /** Class applied to the wrapper around the portal content. */
  className?: string;
  /** Body content of the drawer. */
  children: ReactNode;
  /** Replaces the built-in header (close icon + `headerButton`) entirely. */
  customHeader?: ReactNode;
}

/**
 * Slide-in side panel rendered into a portal — used for detail views,
 * settings, and side content that overlays the main app.
 *
 * Controlled: pass `isOpen` and `handleCloseClick`. Configure which
 * interactions can close the drawer via `closeTriggers` (close button,
 * background click, Escape key — all three by default).
 */
export const Drawer = ({
  className,
  headerButton,
  handleCloseClick,
  customBackgroundClickHandler,
  closeTriggers = [
    CloseTrigger.CloseButtonClick,
    CloseTrigger.BackgroundClick,
    CloseTrigger.EscapeKey,
  ],
  isOpen,
  children,
  closeIcon = true,
  customHeader,
  ...rest
}: DrawerProps) => {
  const closeTriggersArray = Array.isArray(closeTriggers)
    ? closeTriggers
    : [closeTriggers];

  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseClick();
      }
    };

    if (closeTriggersArray.includes(CloseTrigger.EscapeKey) && isOpen) {
      window.addEventListener('keyup', keyHandler);
    }

    return () => {
      window.removeEventListener('keyup', keyHandler);
    };
  }, [isOpen, handleCloseClick]);

  const handleBackgroundOverlayClick = () => {
    if (closeTriggersArray.includes(CloseTrigger.BackgroundClick)) {
      customBackgroundClickHandler
        ? customBackgroundClickHandler()
        : handleCloseClick();
    }
  };

  const handleCloseButtonClick = () => {
    if (closeTriggersArray.includes(CloseTrigger.CloseButtonClick)) {
      handleCloseClick();
    }
  };

  return (
    <Portal>
      <div className={className}>
        <div
          onClick={handleBackgroundOverlayClick}
          className={clsx(styles.backgroundOverlay, {
            [styles.backgroundOverlayOpen]: isOpen,
          })}
          data-testid="background-overlay"
        />
        <section
          data-testid="drawer-container"
          data-drawer-container
          className={clsx(styles.container, {
            [styles.containerOpen]: isOpen,
          })}
          {...rest}
        >
          {customHeader ?? (
            <div className={styles.header} data-drawer-header>
              {headerButton ?? <div />}
              {closeIcon && (
                <Button
                  className={styles.closeButton}
                  data-drawer-header-close-btn
                  data-testid="drawer-header-close-btn"
                  onClick={handleCloseButtonClick}
                  iconName="Close"
                  variant="empty"
                />
              )}
            </div>
          )}
          <div className={styles.body} data-drawer-body>
            {children}
          </div>
        </section>
      </div>
    </Portal>
  );
};
